/**
 * ProjectContext.jsx
 * Resolves and provides the current project for the selected site.
 *
 * On site selection, calls GET /api/get_projects?site_id=...&caller_email=...
 *   - If exactly one project returned: auto-selects silently
 *   - If multiple: surfaces a picker modal
 *   - If none: currentProject remains null
 *
 * Usage:
 *   const { currentProject, resolveProject, clearProject } = useProject();
 */

import { createContext, useContext, useState, useCallback } from 'react';
import { useUser } from './UserContext';

import { apiFetch } from '../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

const ProjectContext = createContext({
  currentProject: null,
  availableProjects: [],
  projectLoading: false,
  showProjectPicker: false,
  resolveProject: () => {},
  selectProject: () => {},
  clearProject: () => {},
});

export function ProjectProvider({ children }) {
  const { callerEmail } = useUser();
  const [currentProject, setCurrentProject] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [showProjectPicker, setShowProjectPicker] = useState(false);

  /**
   * Call after a site is selected. Fetches projects for that site
   * and auto-selects if exactly one. Otherwise shows picker.
   */
  const resolveProject = useCallback(async (siteId) => {
    if (!siteId || !callerEmail) {
      setCurrentProject(null);
      setAvailableProjects([]);
      setShowProjectPicker(false);
      return;
    }

    setProjectLoading(true);
    setShowProjectPicker(false);

    try {
      const params = new URLSearchParams({
        site_id: siteId,
        caller_email: callerEmail,
      });
      const res = await apiFetch(`${API_BASE_URL}/get_projects?${params}`);
      if (!res.ok) throw new Error(`get_projects failed: ${res.status}`);
      const projects = await res.json();

      setAvailableProjects(projects);

      if (projects.length === 1) {
        setCurrentProject(projects[0]);
        setShowProjectPicker(false);
      } else if (projects.length > 1) {
        setCurrentProject(null);
        setShowProjectPicker(true);
      } else {
        setCurrentProject(null);
        setShowProjectPicker(false);
      }
    } catch (err) {
      console.error('ProjectContext: resolveProject failed', err);
      setCurrentProject(null);
      setAvailableProjects([]);
      setShowProjectPicker(false);
    } finally {
      setProjectLoading(false);
    }
  }, [callerEmail]);

  /** Manually select a project (from the picker). */
  const selectProject = useCallback((project) => {
    setCurrentProject(project);
    setShowProjectPicker(false);
  }, []);

  /** Clear project state (e.g. when navigating away). */
  const clearProject = useCallback(() => {
    setCurrentProject(null);
    setAvailableProjects([]);
    setShowProjectPicker(false);
  }, []);

  return (
    <ProjectContext.Provider value={{
      currentProject,
      availableProjects,
      projectLoading,
      showProjectPicker,
      resolveProject,
      selectProject,
      clearProject,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  return useContext(ProjectContext);
}
